import { isAxiosError } from 'axios';
import { useCallback } from 'react';

import {
  isFetchResponseError,
  logAndAddError,
} from '@dotfile/frontend/shared/common';
import {
  useToast,
  UseToastOptions,
} from '@dotfile/frontend/shared/design-system';

/**
 * Extract a human-readable description from various error types
 */
const getErrorDescription = (error: unknown): string => {
  try {
    // For most authentication errors
    if (
      isFetchResponseError(error) &&
      typeof error.cause.jsonBody?.['message'] === 'string'
    ) {
      return error.cause.jsonBody?.['message'];
    }

    // For file upload / download errors
    if (
      isAxiosError(error) &&
      typeof error.response?.data?.message === 'string'
    ) {
      return error.response?.data?.message;
    }

    // Other kind of errors
    if (error instanceof Error) {
      return error.message;
    }
    if (
      error &&
      typeof error === 'object' &&
      'message' in error &&
      typeof error.message === 'string'
    ) {
      return error.message;
    }
  } catch (extractionError) {
    // Since we are already in error-handling, not much we can do except logging it
    // eslint-disable-next-line no-console
    console.error(
      `Unable to extract description from error: ${extractionError}`,
      error,
    );
  }

  // Fallback to simple stringification of the error
  return `${error}`;
};

export type HandleErrorFn = (param: HandleErrorParam) => void;

type HandleErrorParam = {
  /**
   * The caught error, should be an instance of Error
   */
  error: unknown;

  /**
   * Toast title, without any trailing punctuation
   */
  title: string;

  /**
   * toast display duration
   */
  duration?: UseToastOptions['duration'];

  /**
   * Will append "please retry later" to the toast title
   */
  // @TODO - E-69 - better handle of error
  // Ideally this should also be computed from the exact error: some error like NetworkError are always retryable, some like BadRequest are never retryable
  isRetryable?: boolean;

  /**
   * Skip logging and sending the error to Datadog RUM
   */
  skipRUM?: boolean;

  /**
   * Skip toast (if true no toast is displayed)
   */
  skipToast?: boolean;
};

/**
 *
 * @returns
 */
export const useHandleError = () => {
  const toast = useToast();

  return useCallback(
    ({
      error,
      title,
      duration,
      isRetryable,
      skipRUM,
      skipToast,
    }: HandleErrorParam): void => {
      if (!skipRUM) {
        logAndAddError(error);
      }
      // @TODO - E-69 - better handle of error
      if (!skipToast) {
        toast({
          status: 'error',
          title: isRetryable
            ? `${title}, please retry later or contact Dotfile support if the problem persist.`
            : title.endsWith('.')
              ? title
              : `${title}.`,
          description: getErrorDescription(error),
          duration,
        });
      }
    },
    [toast],
  );
};
